import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { AnimatePresence } from "framer-motion";
import { Observer, observer } from "mobx-react-lite";

import { usePlayground } from "../playground-store";
import AnimatedElement from "./AnimatedElement";
import useMessageDragAndDrop from "./hooks/useMessageDragAndDrop";
import useMessageScrolling from "./hooks/useMessageScrolling";
import Message, { MessagePlaceholder } from "./Message";
import { IMessageStore } from "./Message/message-store";
import MessagesFooter from "./MessagesFooter";
import MessagesHeader from "./MessagesHeader";
import SnippetDialog from "./SnippetDialog";
import { withSnippetDialog } from "./SnippetDialog/snippet-store";

interface MessagesProps {
  messages: IMessageStore[];
  onAddMessage?: () => void;
}

const MessagesList = observer(() => {
  const playground = usePlayground();
  const { messagesEndRef } = useMessageScrolling();
  const { dragDisabled, handleDragEnd } = useMessageDragAndDrop();

  const shouldShowPlaceholder = playground.isChat && !playground.readonly;

  return (
    <div
      className="absolute z-0 h-full w-full flex-col overflow-x-hidden pt-2"
      key={playground.mode}
    >
      <DragDropContext onDragEnd={handleDragEnd}>
        <AnimatePresence initial={true} mode={"popLayout"}>
          {playground.isChat ? (
            <Droppable droppableId="messages">
              {(provided, outerSnapshot) => {
                return (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`h-full w-full ${
                      outerSnapshot.isDraggingOver
                        ? "rounded-lg bg-blue-50 p-4"
                        : ""
                    }`}
                    role="list"
                    aria-label="Chat messages"
                  >
                    <Observer>
                      {() => (
                        <>
                          {playground.messages.map(
                            (message: IMessageStore, index: number) => (
                              <Draggable
                                key={message.id}
                                draggableId={message.id}
                                index={index}
                                isDragDisabled={dragDisabled}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`${
                                      snapshot.isDragging
                                        ? "rotate-1 scale-105"
                                        : ""
                                    } transition-all duration-200`}
                                    role="listitem"
                                    aria-label={`Message ${index + 1}: ${
                                      message.role
                                    }`}
                                    tabIndex={dragDisabled ? -1 : 0}
                                  >
                                    <AnimatedElement
                                      isEnabled={!snapshot.isDragging}
                                      style={{
                                        zIndex: snapshot.isDragging
                                          ? 9999
                                          : undefined,
                                        position: snapshot.isDragging
                                          ? "relative"
                                          : undefined,
                                      }}
                                    >
                                      <div
                                        className={`h-full w-full rounded-lg bg-white transition-all duration-200 ease-in-out ${
                                          snapshot.isDragging
                                            ? "ring-2 ring-blue-400 ring-offset-2"
                                            : ""
                                        }`}
                                        style={{
                                          isolation: snapshot.isDragging
                                            ? "isolate"
                                            : undefined,
                                        }}
                                      >
                                        <Message
                                          message={message}
                                          index={index}
                                          isCompletion={!playground.isChat}
                                          isFirst={index === 0}
                                          readonly={playground.readonly}
                                          dragHandleProps={
                                            provided.dragHandleProps ??
                                            undefined
                                          }
                                        />
                                      </div>
                                    </AnimatedElement>
                                  </div>
                                )}
                              </Draggable>
                            ),
                          )}
                        </>
                      )}
                    </Observer>
                    {shouldShowPlaceholder && !outerSnapshot.isDraggingOver && (
                      <AnimatedElement key="placeholder">
                        <div className=" w-full pb-1">
                          <MessagePlaceholder />
                        </div>
                      </AnimatedElement>
                    )}
                    {provided.placeholder}
                    <div ref={messagesEndRef} />
                  </div>
                );
              }}
            </Droppable>
          ) : (
            <>
              {playground.messages.map(
                (message: IMessageStore, index: number) => (
                  <AnimatedElement key={message.id}>
                    <div className=" w-full">
                      <Message
                        message={message}
                        index={index}
                        isCompletion={!playground.isChat}
                        isFirst={index === 0}
                        readonly={playground.readonly}
                      />
                    </div>
                  </AnimatedElement>
                ),
              )}
              {shouldShowPlaceholder && (
                <AnimatedElement key="placeholder" className="h-full">
                  <div className="h-full w-full">
                    <MessagePlaceholder />
                  </div>
                </AnimatedElement>
              )}
            </>
          )}
        </AnimatePresence>
      </DragDropContext>
    </div>
  );
});

const Messages = (_: MessagesProps) => {
  const playground = usePlayground();
  const { containerRef } = useMessageScrolling();

  return (
    <>
      <div className="flex h-full w-full flex-col">
        <div
          className="flex h-full w-full flex-col overflow-y-hidden scroll-smooth"
          ref={containerRef}
        >
          {!playground.readonly && <MessagesHeader />}
          <div className="relative h-full flex-1 overflow-y-auto ">
            <MessagesList />
          </div>
        </div>
        <MessagesFooter />
      </div>
      <SnippetDialog />
    </>
  );
};

export default observer(withSnippetDialog(Messages));
